import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  Chip,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import { TabelaAcao, ColunaTabelaAcao } from '@/components/common/TabelaAcao'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import { pendenciaService } from '@/services/pendenciaService'
import { oportunidadeService } from '@/services/oportunidadeService'
import { usuarioService } from '@/services/usuarioService'
import { mascararCnpj } from '@/services/cnpjService'
import { authStore } from '@/store/authStore'
import { papelPrincipal } from '@/components/layout/menu'
import type { Pendencia, PendenciaStatus } from '@/types/pendencia'
import type { DocumentoObrigatorio } from '@/utils/docsObrigatorios'

interface ReservaInfo {
  empresa: string
  produto: string
  cnpj: string | null
  consultorId: string | null
}

const STATUS_COR: Record<PendenciaStatus, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  ABERTA: 'warning',
  VISUALIZADA: 'info',
  RESPONDIDA: 'info',
  EM_VALIDACAO: 'warning',
  RESOLVIDA: 'success',
  REABERTA: 'warning',
  CANCELADA: 'default',
}

const STATUS_LABEL: Record<PendenciaStatus, string> = {
  ABERTA: 'Aberta',
  VISUALIZADA: 'Visualizada',
  RESPONDIDA: 'Respondida',
  EM_VALIDACAO: 'Em validação',
  RESOLVIDA: 'Resolvida',
  REABERTA: 'Reaberta',
  CANCELADA: 'Cancelada',
}

// "Em aberto" = ainda demanda ação (não resolvida/cancelada).
const ABERTAS: PendenciaStatus[] = ['ABERTA', 'VISUALIZADA', 'RESPONDIDA', 'EM_VALIDACAO', 'REABERTA']

/** Pendência que ainda espera resposta do consultor — a ação é "Responder →". */
const RESPONDIVEIS: PendenciaStatus[] = ['ABERTA', 'VISUALIZADA', 'REABERTA']

const fmtData = (iso?: string) => (iso ? new Date(iso).toLocaleDateString('pt-BR') : '—')

/**
 * Bloqueante (V27, mesma regra do card "Bloqueantes" de `GET /v1/pendencias/resumo`):
 * pendência que ainda espera o consultor E cujo documento solicitado é item obrigatório do
 * checklist do produto da reserva — comparação por tipo OU rótulo, porque o campo é texto
 * livre do operador. Se o backend passar a mandar `bloqueante`, ele vence a derivação.
 */
export const ehBloqueante = (
  p: Pendencia,
  checklistDaReserva: DocumentoObrigatorio[] | undefined,
): boolean => {
  if (typeof p.bloqueante === 'boolean') return p.bloqueante
  if (!RESPONDIVEIS.includes(p.status) || !p.documentoSolicitado) return false
  const doc = p.documentoSolicitado
  return (checklistDaReserva ?? []).some((item) => item.tipo === doc || item.label === doc)
}

type Filtro = 'abertas' | 'bloqueantes' | 'todas'

interface Props {
  /**
   * Onda 2 / A04: renderizada como aba de "Pendências e Documentos", que já traz
   * PageHeader e KPIs — o cabeçalho local sai.
   */
  embutida?: boolean
}

/**
 * Inbox de pendências do consultor. A tabela segue o Figma 26:202: Solicitação · Empresa
 * · Produto · Prazo · Status · Checklist · Ação. O chip "Bloqueante" e o filtro
 * "Só bloqueantes" (Onda 8 / A16) usam a regra V27 do resumo — ver `ehBloqueante`. Para o
 * back-office (ADMIN/GERENTE/OPERADOR) a tabela ganha CNPJ do cliente e Consultor (11/09, pedido
 * do negócio): o CNPJ vem da reserva; o nome do consultor, da listagem de usuários (só ADMIN
 * enxerga — para os demais fica "—"). Empresa e produto vêm da MESMA listagem de reservas
 * do consultor (`GET /v1/oportunidades`, já escopada no backend) — a pendência só carrega
 * o `oportunidadeId`; se a listagem falhar, as colunas mostram "—" em vez de derrubar a
 * inbox.
 */
const MinhasPendenciasPage: FC<Props> = ({ embutida = false }) => {
  const navigate = useNavigate()
  const [todas, setTodas] = useState<Pendencia[]>([])
  const [reservaInfo, setReservaInfo] = useState<Record<string, ReservaInfo>>({})
  const [nomesUsuarios, setNomesUsuarios] = useState<Record<string, string>>({})
  const usuario = authStore((s) => s.usuario)
  const papel = papelPrincipal(usuario?.permissoes)
  /** ADMIN, GERENTE e OPERADOR veem CNPJ e consultor; o consultor vê a tabela do Figma. */
  const backoffice = papel === 'ADMIN' || papel === 'GERENTE' || papel === 'OPERADOR'
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [erroStatus, setErroStatus] = useState<number | null>(null)
  const [checklists, setChecklists] = useState<Record<string, DocumentoObrigatorio[]>>({})
  const [filtro, setFiltro] = useState<Filtro>('abertas')

  const carregar = useCallback(async () => {
    setCarregando(true)
    setErro(null)
    setErroStatus(null)
    try {
      const [pendencias, oportunidadesPage, usuarios] = await Promise.all([
        pendenciaService.minhas(),
        // Resolve empresa/produto/CNPJ/consultor; falha aqui não derruba a inbox.
        oportunidadeService.listar(0, 200).catch(() => null),
        // Nome do consultor (back-office): a listagem de usuários é só do ADMIN; 403 vira "—".
        backoffice ? usuarioService.listar({ max: 500 }).catch(() => []) : Promise.resolve([]),
      ])
      setTodas(pendencias)
      const info: Record<string, ReservaInfo> = {}
      for (const o of oportunidadesPage?.content ?? []) {
        info[o.id] = {
          empresa: o.razaoSocial ?? '—',
          produto: o.tipoNecessidade ?? '—',
          cnpj: o.cnpj ?? null,
          consultorId: o.usuarioResponsavel ?? null,
        }
      }
      setReservaInfo(info)
      setNomesUsuarios(Object.fromEntries(usuarios.map((u) => [u.id, u.nome])))

      // Checklist só das reservas com pendência respondível que pede documento — é o
      // que define "bloqueante". Falha de um checklist não derruba a inbox: a pendência
      // fica sem o chip (mesmo comportamento de `bloqueante` ausente).
      const precisaChecklist = pendencias.some((p) => typeof p.bloqueante !== 'boolean')
      if (precisaChecklist) {
        const ids = [
          ...new Set(
            pendencias
              .filter((p) => RESPONDIVEIS.includes(p.status) && p.documentoSolicitado)
              .map((p) => p.oportunidadeId),
          ),
        ]
        const resultados = await Promise.all(
          ids.map((id) =>
            oportunidadeService.documentosObrigatorios(id).catch(() => [] as DocumentoObrigatorio[]),
          ),
        )
        const mapa: Record<string, DocumentoObrigatorio[]> = {}
        ids.forEach((id, i) => {
          mapa[id] = resultados[i]
        })
        setChecklists(mapa)
      } else {
        setChecklists({})
      }
    } catch (e) {
      setErro('Não foi possível carregar suas pendências.')
      setErroStatus((e as { status?: number })?.status ?? null)
    } finally {
      setCarregando(false)
    }
  }, [backoffice])

  useEffect(() => {
    void carregar()
  }, [carregar])

  const bloqueantes = useMemo(() => {
    const set = new Set<string>()
    for (const p of todas) if (ehBloqueante(p, checklists[p.oportunidadeId])) set.add(p.id)
    return set
  }, [todas, checklists])

  const lista = useMemo(() => {
    if (filtro === 'abertas') return todas.filter((p) => ABERTAS.includes(p.status))
    if (filtro === 'bloqueantes') return todas.filter((p) => bloqueantes.has(p.id))
    return todas
  }, [todas, filtro, bloqueantes])
  const qtdAbertas = useMemo(() => todas.filter((p) => ABERTAS.includes(p.status)).length, [todas])

  // Tabela do Figma: Solicitação · Empresa · Produto · Prazo · Status · Ação.
  const colunas: ColunaTabelaAcao<Pendencia>[] = [
    {
      titulo: 'Solicitação',
      render: (p) => (
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" fontWeight={600}>
              {p.titulo}
            </Typography>
            {bloqueantes.has(p.id) && (
              <Tooltip title="Documento obrigatório do checklist do produto: sem ele a reserva não avança.">
                <Chip size="small" color="error" variant="outlined" label="Bloqueante" />
              </Tooltip>
            )}
          </Stack>
          {(p.documentoSolicitado || p.descricao) && (
            <Typography variant="caption" color="text.secondary">
              {p.documentoSolicitado || p.descricao}
            </Typography>
          )}
        </Box>
      ),
    },
    { titulo: 'Empresa', render: (p) => reservaInfo[p.oportunidadeId]?.empresa ?? '—' },
    // Back-office (11/09): CNPJ do cliente da reserva e nome do consultor responsável.
    ...(backoffice
      ? ([
          {
            titulo: 'CNPJ',
            render: (p) => {
              const cnpj = reservaInfo[p.oportunidadeId]?.cnpj
              return cnpj ? mascararCnpj(cnpj) : '—'
            },
          },
          {
            titulo: 'Consultor',
            render: (p) => {
              const id = reservaInfo[p.oportunidadeId]?.consultorId ?? p.consultorId ?? null
              return (id && nomesUsuarios[id]) || '—'
            },
          },
        ] as ColunaTabelaAcao<Pendencia>[])
      : []),
    { titulo: 'Produto', render: (p) => reservaInfo[p.oportunidadeId]?.produto ?? '—' },
    { titulo: 'Prazo', render: (p) => fmtData(p.prazoResposta) },
    {
      titulo: 'Status',
      render: (p) => (
        <Chip size="small" color={STATUS_COR[p.status]} label={STATUS_LABEL[p.status]} />
      ),
    },
    {
      titulo: 'Checklist',
      render: (p) =>
        bloqueantes.has(p.id) ? (
          <Button
            size="small"
            variant="text"
            sx={{ whiteSpace: 'nowrap' }}
            onClick={() => navigate(`/reservas/${p.oportunidadeId}/estruturacao?etapa=3`)}
          >
            Ver checklist
          </Button>
        ) : (
          '—'
        ),
    },
  ]

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          {!embutida && <Typography variant="h5">Minhas Pendências</Typography>}
          {qtdAbertas > 0 && <Chip color="warning" size="small" label={`${qtdAbertas} em aberto`} />}
        </Stack>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={filtro}
          onChange={(_, v) => v && setFiltro(v)}
        >
          <ToggleButton value="abertas">Em aberto</ToggleButton>
          <ToggleButton value="bloqueantes">Só bloqueantes ({bloqueantes.size})</ToggleButton>
          <ToggleButton value="todas">Todas</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {erro ? (
        <EstadoDeLista
          estado={erroStatus === 403 ? 'semPermissao' : 'erro'}
          {...(erroStatus !== 403 && {
            mensagem: erro,
            acao: { label: 'Tentar novamente', onClick: () => void carregar() },
          })}
        />
      ) : carregando ? (
        <EstadoDeLista estado="carregando" mensagem="Carregando pendências…" />
      ) : lista.length === 0 ? (
        <EstadoDeLista
          estado="vazio"
          mensagem={
            filtro === 'abertas'
              ? 'Nenhuma pendência em aberto'
              : filtro === 'bloqueantes'
                ? 'Nenhuma pendência bloqueante'
                : 'Nenhuma pendência'
          }
          detalhe={
            filtro === 'abertas'
              ? 'Quando a Originei solicitar algo, aparece aqui.'
              : filtro === 'bloqueantes'
                ? 'Bloqueante é a pendência cujo documento é obrigatório no checklist do produto.'
                : undefined
          }
        />
      ) : (
        <TabelaAcao
          aria-label="Minhas pendências"
          colunas={colunas}
          linhas={lista}
          chaveDaLinha={(p) => p.id}
          onAbrir={(p) => navigate(`/reservas/${p.oportunidadeId}/pendencias`)}
          renderAcao={(p) => (RESPONDIVEIS.includes(p.status) ? 'Responder →' : 'Detalhes →')}
        />
      )}
    </Box>
  )
}

export default MinhasPendenciasPage
